import React, { useState } from 'react';
import { Plus, Trash2, Pencil, Users, Shield, Eye, Search, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { useRoles } from '../api/roles/use-roles';
import { Role } from '../models/role';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { StatusBadge } from '@/components/shared/StatusBadge';

const RolesManagement: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { roles, isLoading, error, deleteRole } = useRoles();

    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<'all' | 'system' | 'custom'>('all');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

    const filteredRoles =
        roles?.filter(role => {
            const matchesSearch =
                role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (role.description &&
                    role.description.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesFilter = filterType === 'all' || role.type === filterType;
            return matchesSearch && matchesFilter;
        }) || [];

    const handleDeleteClick = (role: Role) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!roleToDelete) return;

        try {
            await deleteRole.mutateAsync(roleToDelete.id);
            enqueueSnackbar('Role deleted successfully', { variant: 'success' });
        } catch (error: any) {
            const message = error.response?.data?.message || 'An error occurred';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setRoleToDelete(null);
        }
    };

    const getPermissionSummary = (permissions: Record<string, any>) => {
        const modules = Object.keys(permissions);
        const totalPermissions = modules.length * 6 * 4; // modules * actions * scopes
        let grantedPermissions = 0;

        modules.forEach(module => {
            const modulePerms = permissions[module];
            ['view', 'create', 'edit', 'delete', 'import', 'export'].forEach(action => {
                ['mine', 'my_reports', 'my_team', 'all'].forEach(scope => {
                    if (modulePerms[action]?.[scope]) {
                        grantedPermissions++;
                    }
                });
            });
        });

        return {
            total: totalPermissions,
            granted: grantedPermissions,
            percentage: Math.round((grantedPermissions / totalPermissions) * 100),
        };
    };

    const getRoleIcon = (role: Role) => {
        if (role.is_system) {
            return <Shield className="h-5 w-5 text-primary" />;
        }
        return <Users className="h-5 w-5 text-muted-foreground" />;
    };

    if (isLoading) {
        return (
            <PageContainer>
                <div className="flex justify-between items-center">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-32" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <Card key={i}>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <Skeleton className="h-6 w-32" />
                                    <Skeleton className="h-6 w-16" />
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                                <div className="flex justify-between items-center">
                                    <Skeleton className="h-4 w-20" />
                                    <Skeleton className="h-8 w-8 rounded" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center space-y-4">
                            <Shield className="h-12 w-12 text-destructive mx-auto" />
                            <div>
                                <h3 className="text-lg font-semibold text-destructive">
                                    Error Loading Roles
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Unable to load roles. Please try again later.
                                </p>
                            </div>
                            <Button onClick={() => window.location.reload()}>Try Again</Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="Roles"
                description="Manage user roles and permissions across the system"
                actions={
                    <Button onClick={() => navigate('/roles/create')}>
                        <Plus className="h-4 w-4" />
                        Create Role
                    </Button>
                }
            />

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search roles..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant={filterType === 'all' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilterType('all')}
                            >
                                All
                            </Button>
                            <Button
                                variant={filterType === 'system' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilterType('system')}
                            >
                                System
                            </Button>
                            <Button
                                variant={filterType === 'custom' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilterType('custom')}
                            >
                                Custom
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats */}
            <StatTileGrid className="md:grid-cols-3">
                <StatTile
                    label="Total Roles"
                    value={roles?.length || 0}
                    icon={Users}
                    tone="brand"
                />
                <StatTile
                    label="System Roles"
                    value={roles?.filter(r => r.type === 'system').length || 0}
                    icon={Shield}
                    tone="success"
                />
                <StatTile
                    label="Custom Roles"
                    value={roles?.filter(r => r.type === 'custom').length || 0}
                    icon={Users}
                    tone="info"
                />
            </StatTileGrid>

            {/* Roles Grid */}
            {filteredRoles.length === 0 ? (
                <Card>
                    <CardContent className="pt-6">
                        <div className="text-center py-12">
                            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">No roles found</h3>
                            <p className="text-muted-foreground mb-4">
                                {searchTerm || filterType !== 'all'
                                    ? 'Try adjusting your search or filters'
                                    : 'Get started by creating your first role'}
                            </p>
                            {!searchTerm && filterType === 'all' && (
                                <Button onClick={() => navigate('/roles/create')}>
                                    <Plus className="h-4 w-4" />
                                    Create Role
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredRoles.map(role => {
                        const permissionSummary = getPermissionSummary(role.permissions);
                        return (
                            <Card key={role.id} className="hover:shadow-md transition-shadow">
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            {getRoleIcon(role)}
                                            <div className="flex-1 min-w-0">
                                                <CardTitle className="truncate text-base">
                                                    {role.name}
                                                </CardTitle>
                                                <StatusBadge
                                                    status={role.type}
                                                    kind="account"
                                                    className="mt-1"
                                                />
                                            </div>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label={`Actions for ${role.name}`}
                                                    title="Actions"
                                                >
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                    onClick={() => navigate(`/roles/${role.id}`)}
                                                >
                                                    <Eye className="me-2 h-4 w-4" />
                                                    View Details
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() =>
                                                        navigate(`/roles/${role.id}/edit`)
                                                    }
                                                >
                                                    <Pencil className="me-2 h-4 w-4" />
                                                    Edit Role
                                                </DropdownMenuItem>
                                                {!role.is_system && (
                                                    <>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            onClick={() => handleDeleteClick(role)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 className="me-2 h-4 w-4" />
                                                            Delete Role
                                                        </DropdownMenuItem>
                                                    </>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                        {role.description || 'No description provided'}
                                    </p>

                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">
                                                Permissions
                                            </span>
                                            <span className="font-medium">
                                                {permissionSummary.granted}/
                                                {permissionSummary.total}
                                            </span>
                                        </div>
                                        <div className="w-full bg-muted rounded-full h-2">
                                            <div
                                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${permissionSummary.percentage}%`,
                                                }}
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {permissionSummary.percentage}% of permissions granted
                                        </p>
                                    </div>

                                    <div className="flex items-center justify-between pt-2">
                                        <div className="text-xs text-muted-foreground">
                                            Updated <DateTimeFormatter date={role.updated_at} />
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate(`/roles/${role.id}`)}
                                            >
                                                <Eye className="h-3 w-3" />
                                                View
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate(`/roles/${role.id}/edit`)}
                                            >
                                                <Pencil className="h-3 w-3" />
                                                Edit
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Role</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete the role "{roleToDelete?.name}"? This
                            action cannot be undone and will affect all users assigned to this role.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteConfirm} variant="destructive">
                            Delete Role
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </PageContainer>
    );
};

export default RolesManagement;
